import './tenant-licenses.scss'

import { Button, Checkbox, Descriptions, notification, Space, Typography } from 'antd'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
import { CheckboxValueType } from 'antd/lib/checkbox/Group'
import axios from 'axios'
import React, { useEffect, useState } from 'react'
import { Column } from 'react-display-flex'
import useSWR from 'swr'

import { fetchDetail } from '../../../api'
import { getEnvironmentHost } from '../../../util/environment'
import { licenses } from '../codes'
import { messages } from '../messages'

const CheckboxGroup = Checkbox.Group
const { Item } = Descriptions

interface Props {
  tenantId: number
  onSuccess: () => void
}

const onUpdateLicenses = async ({ tenantId, current, changed, onSuccess }) => {
  const add = changed.filter((license: string) => !current.includes(license))
  const remove = current.filter((license: string) => !changed.includes(license))

  const changes = [...add, ...remove].map((license: string) => ({
    licence: license,
    enabled: changed.includes(license),
  }))

  if (!changes.length) {
    notification.info({ message: messages.common.info, description: messages.licenses.update.unchanged })
    return false
  }

  const licensesPromises = changes.map(async (payload) =>
    Promise.resolve(axios.put(`/tenants/${tenantId}/licences`, payload)),
  )

  try {
    await Promise.all(licensesPromises)
    notification.success({ message: messages.common.success, description: messages.licenses.update.success })
    onSuccess()
  } catch (error) {
    notification.error({ message: messages.common.error, description: messages.licenses.update.error })
  }

  return changes
}

export const UpdateTenantLicenses = ({ tenantId, onSuccess }: Props) => {
  const { data }: any = useSWR(tenantId ? `/tenants/${tenantId}` : null, fetchDetail, {
    suspense: true,
  })
  const { licences: currentLicenses, name, segmentCode, domain } = data || {}

  const [checkedList, setCheckedList] = useState(currentLicenses)
  const [indeterminate, setIndeterminate] = useState(true)
  const [checkAll, setCheckAll] = useState(false)

  useEffect(() => {
    setCheckedList(currentLicenses)
  }, [currentLicenses])

  const onChange = (list: CheckboxValueType[]) => {
    setCheckedList(list)
    setIndeterminate(!!list.length && list.length < licenses.length)
    setCheckAll(list.length === licenses.length)
  }

  const onCheckAllChange = (event: CheckboxChangeEvent) => {
    setCheckedList(event.target.checked ? licenses : [])
    setIndeterminate(false)
    setCheckAll(event.target.checked)
  }

  return (
    <Space direction="vertical">
      <Typography.Title level={4}>{name}</Typography.Title>
      <Descriptions>
        <Item label="ID">{tenantId}</Item>
        <Item label="Segment">{segmentCode}</Item>
        <Item label="Domain">
          <Typography.Link href={getEnvironmentHost({ domain })} target="_blank">
            {domain}
          </Typography.Link>
        </Item>
      </Descriptions>
      <Checkbox indeterminate={indeterminate} onChange={onCheckAllChange} checked={checkAll}>
        Check all
      </Checkbox>
      <CheckboxGroup options={licenses} value={checkedList} onChange={onChange} className="licenses-checkboxes" />
      <Column>
        <Button
          type="primary"
          onClick={() => onUpdateLicenses({ tenantId, current: currentLicenses, changed: checkedList, onSuccess })}
        >
          Update Licenses
        </Button>
      </Column>
    </Space>
  )
}
